import React, { useState, useEffect, useMemo } from 'react';
import '../styles/App.css';
import '../styles/Faq.css';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import {Link, useLocation, useParams} from 'react-router-dom';
import { getLocalizedFaqData } from '../data/FaqData';
import FaqBannerSearchBar from "../components/FaqBannerSearchBar";
import {useTranslation} from "react-i18next";
import FaqAnswer from "../components/FaqAnswer";
import {capitalizeFirstLetter, getPath, getSiteLanguage} from "../utils/functions";

function FaqSectionPage() {
  const [t, i18n] = useTranslation();
  // Getting the section name from the URL (e.g., "profile-management")
  const { sectionName } = useParams();
  const location = useLocation();
  const siteLanguage = getSiteLanguage(location.pathname);
  const sectionTitleKeys = {
    "general": "topic1",
    "profile-management": "topic2",
    "search-and-filtering": "topic3",
    "activities-details": "topic4",
    "booking-schedule": "topic5",
    "reviews&comments": "topic6",
  };

  // State variables (like variables in JS, but React tracks changes to them)
  const [activeQuestion, setActiveQuestion] = useState(null); // Tracks which question is open
  const [activeSection, setActiveSection] = useState(null); // Tracks which section is active
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false); // Tracks mobile dropdown state
  const [windowWidth, setWindowWidth] = useState(window.innerWidth); // Tracks screen width
  const [mobileOrder, setMobileOrder] = useState([]); // Tracks order of items in mobile dropdown

  // Getting the current section's data based on the selected language.
  const localizedFaqData = useMemo(() => getLocalizedFaqData(siteLanguage), [siteLanguage]);
  const currentSection = localizedFaqData[sectionName];

  // Creating an array of subsections (e.g., "My Account", "My Profiles")
  const subsections = Object.keys(currentSection || {}).map((title, index) => ({
    id: index + 1, // Unique ID for each subsection
    title // Title of the subsection
  }));

  // Formatting the section name for use in IDs (removes hyphens)
  const sectionKey = sectionName.replace("-", "");


  // Effect to handle window resizing (like an event listener in JS)
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth); // Updates window width on resize
    window.addEventListener('resize', handleResize); // Adds event listener
    return () => window.removeEventListener('resize', handleResize); // Cleans up listener
  }, []); // Empty array means this runs only once when the component loads

  // Effect to handle mobile vs desktop layout
  useEffect(() => {
    if (currentSection) {
      if (windowWidth <= 768) { // Mobile layout
        setMobileOrder([...subsections]); // Sets initial order for mobile dropdown
        if (!activeSection) {
          setActiveSection(`${sectionKey}Section1`); // Activates first section by default
        }
      } else { // Desktop layout
        setActiveSection(`${sectionKey}Section1`); // Activates first section
      }
    }
  }, [windowWidth, sectionName, currentSection]); // Runs when these values change

  // Effect to reset active question when section changes
  useEffect(() => {
    setActiveQuestion(null);
  }, [sectionName, siteLanguage]);

  // Function to toggle questions open/closed
  const handleQuestionToggle = (questionId) => {
    setActiveQuestion(activeQuestion === questionId ? null : questionId);
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId); // Sets the active section
    if (windowWidth <= 768) { // Mobile-specific behavior
      const selectedSubsection = subsections.find(sub => 
        `${sectionKey}Section${sub.id}` === sectionId
      );
      // Reorders the dropdown with the selected item at the top
      const newOrder = [
        selectedSubsection,
        ...subsections.filter(sub => 
          `${sectionKey}Section${sub.id}` !== sectionId
        )
      ];
      setMobileOrder(newOrder); // Updates the order
      setMobileDropdownOpen(false); // Closes the dropdown
    }
  };

  // If the section isn't found in the data, show an error message
  if (!currentSection) return <div>Section not found</div>;

  return (
      <>
        <Navbar/>
        <div className="faq-page-wrapper">
          <FaqBannerSearchBar />

          {/* Main content area */}
          <div className="faq-container container">
            {/* Breadcrumbs navigation */}
            <ol className="breadcrumbs">
              <li itemScope itemType="https://schema.org/WebPage">
                <span itemProp="title" style={{color: "#2F4C72"}}><img src="/Images/question-in-circle.png"
                                                                       style={{width: "30px"}}/>{t('faqs.topics')}</span>
              </li>
              <li itemScope itemType="https://schema.org/WebPage">
                <span itemProp="title"
                      style={{color: "#28AFb0"}}>{t(`faqs.${sectionTitleKeys[sectionName]}`) || capitalizeFirstLetter(sectionName.split("-").join(" "))}</span>
              </li>
            </ol>
            <div id="main" className="container d-flex justify-content-evenly">
              {/* Left sidebar with sections */}
              <section className="faq-topics">
                <ul id="itemList" style={{listStyle: "none"}}>
                  <li
                      className={`main-section active`}
                      onClick={() => windowWidth <= 768 && setMobileDropdownOpen(!mobileDropdownOpen)}
                  >

                    {/* Subsections list */}
                    <ul className={`subsections ${mobileDropdownOpen ? 'active' : ''}`}>
                      {(windowWidth <= 768 ? mobileOrder : subsections).map((subsection) => (
                          <li
                              key={subsection.id}
                              data-target={`${sectionKey}Section${subsection.id}`}
                              className={activeSection === `${sectionKey}Section${subsection.id}` ? 'active' : ''}
                              onClick={() => handleSectionChange(`${sectionKey}Section${subsection.id}`)}
                          >
                            {subsection.title}
                          </li>
                      ))}
                    </ul>
                  </li>
                </ul>
              </section>

              {/* FAQ content sections */}
              {subsections.map((subsection) => {
                const subsectionData = currentSection[subsection.title];
                return (
                    <section
                        key={subsection.id}
                        className="container faq-section"
                        id={`${sectionKey}Section${subsection.id}`}
                        style={{display: activeSection === `${sectionKey}Section${subsection.id}` ? 'block' : 'none'}}
                    >
                      {/* Individual FAQ items */}
                      {subsectionData.map((faq, index) => (
                          <div className="faq-items" key={index}>
                            <button
                                className={`faq-qtn ${activeQuestion === `${subsection.id}-${index}` ? 'active' : ''}`}
                                onClick={() => handleQuestionToggle(`${subsection.id}-${index}`)}
                            >
                              {faq.question}
                              <span className="icon">
                          {activeQuestion === `${subsection.id}-${index}` ? '-' : '+'}
                        </span>
                            </button>
                            <div
                                className="faq-ans"
                                style={{display: activeQuestion === `${subsection.id}-${index}` ? 'block' : 'none'}}
                            >
                              <p><FaqAnswer faq={faq} /></p>
                            </div>
                          </div>
                      ))}
                    </section>
                );
              })}
            </div>
          </div>
        </div>
        {/* Footer */}
        <Footer/>
      </>
  );
}

export default FaqSectionPage;